/* ──────────────────────────────────────────────────────────────────────
 *  AdminUserAccessModal — admin per-user access control.
 *
 *  Opened from a user's row in the Admin Console. Shows the user's rung
 *  (archetype) and what they can see/do, and lets an admin manually override
 *  any feature on/off (or set the rung). NOT the standard progression path —
 *  a manual override / monitor surface.
 *
 *  Each feature is tri-state: Inherit (use the rung default) / On / Off.
 *  "Effective" shows the resulting state; overridden rows are flagged.
 *
 *  System-admin UI → English only (no i18n), per CLAUDE.md.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FEATURE_CATALOG,
  getArchetypeConfig,
  computeUnlockedFeatures,
  ARCHETYPE_KEYS,
} from '../../Data/Onboarding/archetypeConfig';
import type { FeatureFlag, MilestoneKey, ArchetypeKey } from '../../Data/Onboarding/archetypeConfig';
import {
  fetchUserFeatureState,
  setUserFeatureOverrides,
  setUserArchetype,
} from '../../api/adminUserFeatures';
import type { AdminUserFeatureState } from '../../api/adminUserFeatures';
import styles from './AdminUserAccessModal.module.css';

type Tri = 'inherit' | 'on' | 'off';

interface AdminUserAccessModalProps {
  userGuid: string;
  displayName: string;
  screenName?: string;
  onClose: () => void;
}

export default function AdminUserAccessModal({
  userGuid,
  displayName,
  screenName,
  onClose,
}: AdminUserAccessModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<AdminUserFeatureState | null>(null);
  const [archetype, setArchetype] = useState<ArchetypeKey>('explorer');
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [filterOverridesOnly, setFilterOverridesOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchUserFeatureState(userGuid)
      .then((s) => {
        if (!alive) return;
        setState(s);
        setArchetype(getArchetypeConfig(s.archetypeSlug ?? 'explorer').key);
        setOverrides({ ...(s.featureOverrides ?? {}) });
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : 'Failed to load user access.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [userGuid]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const baseline = useMemo(() => {
    const cfg = getArchetypeConfig(archetype);
    const ms = new Set<MilestoneKey>((state?.completedMilestones ?? []) as MilestoneKey[]);
    return computeUnlockedFeatures(cfg, ms);
  }, [archetype, state?.completedMilestones]);

  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup: Record<string, typeof FEATURE_CATALOG> = {};
    for (const e of FEATURE_CATALOG) {
      if (!byGroup[e.group]) { byGroup[e.group] = []; order.push(e.group); }
      byGroup[e.group].push(e);
    }
    return order.map((g) => ({ group: g, items: byGroup[g] }));
  }, []);

  const triFor = (flag: FeatureFlag): Tri => {
    const ov = overrides[flag];
    return ov === undefined ? 'inherit' : ov ? 'on' : 'off';
  };
  const effectiveOn = (flag: FeatureFlag): boolean => {
    const ov = overrides[flag];
    return ov === undefined ? baseline.has(flag) : ov;
  };
  const setTri = (flag: FeatureFlag, tri: Tri) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (tri === 'inherit') delete next[flag];
      else next[flag] = tri === 'on';
      return next;
    });
  };

  const overrideCount = Object.keys(overrides).length;
  const archetypeChanged =
    state != null && getArchetypeConfig(state.archetypeSlug ?? 'explorer').key !== archetype;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      if (archetypeChanged) await setUserArchetype(userGuid, archetype);
      await setUserFeatureOverrides(userGuid, overrides);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
      setSaving(false);
    }
  }, [archetypeChanged, archetype, overrides, userGuid, onClose]);

  return (
    <div className={styles.backdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Manage user access"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <div className={styles.identity}>
            <span className={styles.name}>{displayName}</span>
            {screenName && <span className={styles.screen}>@{screenName}</span>}
          </div>
          <button className={styles.x} onClick={onClose} aria-label="Close" type="button">✕</button>
        </div>

        {loading ? (
          <div className={styles.body}><div className={styles.note}>Loading…</div></div>
        ) : !state?.hasProfile ? (
          <div className={styles.body}>
            <div className={styles.note}>
              This user has no onboarding profile, so access can&apos;t be managed here yet.
            </div>
          </div>
        ) : (
          <>
            <div className={styles.body}>
              <div className={styles.section}>Rung (archetype)</div>
              <div className={styles.seg}>
                {ARCHETYPE_KEYS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={archetype === k ? styles.segOn : ''}
                    onClick={() => setArchetype(k)}
                  >
                    {k.charAt(0).toUpperCase() + k.slice(1)}
                  </button>
                ))}
              </div>
              <div className={styles.note} style={{ marginTop: 6 }}>
                Original: {state.originalArchetypeSlug ?? '—'}
                {archetypeChanged ? ' · changed (overrides the normal progression)' : ''}
              </div>

              {state.completedMilestones.length > 0 && (
                <>
                  <div className={styles.section} style={{ marginTop: 14 }}>Completed milestones</div>
                  <div className={styles.chips}>
                    {state.completedMilestones.map((m) => (
                      <span key={m} className={styles.chip}>{m}</span>
                    ))}
                  </div>
                </>
              )}

              <div className={styles.featbar}>
                <div className={styles.section} style={{ margin: 0 }}>Access — what they can see &amp; do</div>
                <div className={styles.filter}>
                  <button
                    type="button"
                    className={!filterOverridesOnly ? styles.filterOn : ''}
                    onClick={() => setFilterOverridesOnly(false)}
                  >All</button>
                  <button
                    type="button"
                    className={filterOverridesOnly ? styles.filterOn : ''}
                    onClick={() => setFilterOverridesOnly(true)}
                  >Overrides only</button>
                </div>
              </div>
              <div className={styles.note} style={{ marginBottom: 6 }}>
                Inherited from the rung unless overridden.
              </div>

              {groups.map(({ group, items }) => {
                const rows = items.filter(
                  (it) => !filterOverridesOnly || overrides[it.flag] !== undefined,
                );
                if (rows.length === 0) return null;
                return (
                  <div key={group}>
                    <div className={styles.grpLabel}>{group}</div>
                    {rows.map((it) => {
                      const tri = triFor(it.flag);
                      const eff = effectiveOn(it.flag);
                      const base = baseline.has(it.flag);
                      return (
                        <div key={it.flag} className={`${styles.frow} ${tri !== 'inherit' ? styles.ovr : ''}`}>
                          <div className={styles.fmeta}>
                            <div className={styles.fname}>{it.label}</div>
                            <div className={styles.fbase}>rung: {base ? 'on' : 'off'}</div>
                          </div>
                          <span className={`${styles.eff} ${eff ? styles.enabled : styles.disabled}`}>
                            {eff ? 'Enabled' : 'Disabled'}
                          </span>
                          <div className={styles.tri}>
                            {(['inherit', 'on', 'off'] as Tri[]).map((t) => (
                              <button
                                key={t}
                                type="button"
                                className={tri === t ? styles.triOn : ''}
                                onClick={() => setTri(it.flag, t)}
                              >
                                {t === 'inherit' ? 'Inherit' : t === 'on' ? 'On' : 'Off'}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className={styles.foot}>
              <span className={styles.count}>
                {overrideCount === 0 ? 'No overrides' : `${overrideCount} override${overrideCount > 1 ? 's' : ''} set`}
                {archetypeChanged ? ' · rung changed' : ''}
              </span>
              {overrideCount > 0 && (
                <button type="button" className={styles.linkBtn} onClick={() => setOverrides({})} disabled={saving}>
                  Reset all
                </button>
              )}
              <button type="button" className={styles.btn} onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        )}

        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  );
}
